import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { CheckIcon } from '@untitledui-icons/react/line'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/base/buttons'
import { FormInput, FormSelect, FormSwitch } from '@/components/forms'
import { useCreatePartner } from '@/hooks/api/usePartners'

// Validation schema
const partnerCreateSchema = z.object({
  name: z.string().min(1, 'app.validation.name_required'),
  partner_type: z.enum(['reseller', 'distributor', 'referral']).nullable().optional(),
  hide_referral_link: z.boolean(),
  revenue_share_percentage: z
    .number()
    .min(0, 'app.validation.revenue_share_percentage_range')
    .max(100, 'app.validation.revenue_share_percentage_range')
    .nullable()
    .optional(),
  revenue_share_period_months: z
    .number()
    .min(0, 'app.validation.revenue_share_period_range')
    .max(60, 'app.validation.revenue_share_period_range')
    .nullable()
    .optional(),
})

type PartnerCreateFormValues = z.infer<typeof partnerCreateSchema>

export function PartnerCreatePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Partner type options with translations
  const partnerTypeOptions = useMemo(() => [
    { value: 'reseller', label: t('resources.partners.partner_types.reseller') },
    { value: 'distributor', label: t('resources.partners.partner_types.distributor') },
    { value: 'referral', label: t('resources.partners.partner_types.referral') },
  ], [t])

  // Mutation
  const createPartner = useCreatePartner()

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<PartnerCreateFormValues>({
    resolver: zodResolver(partnerCreateSchema),
    defaultValues: {
      name: '',
      partner_type: null,
      hide_referral_link: false,
      revenue_share_percentage: null,
      revenue_share_period_months: null,
    },
  })

  // Handle form submission
  const onSubmit = async (data: PartnerCreateFormValues) => {
    try {
      await createPartner.mutateAsync({
        name: data.name,
        meta: {
          partner_type: data.partner_type || undefined,
          hide_referral_link: data.hide_referral_link,
          revenue_share_percentage: data.revenue_share_percentage ?? undefined,
          revenue_share_period_months: data.revenue_share_period_months ?? undefined,
        },
      })
      toast.success(t('resources.partners.create_success'))
      navigate('/partners')
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('resources.partners.create_error')
      toast.error(message)
    }
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t('resources.partners.create_title')} subtitle={t('resources.partners.create_subtitle')} />

      <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <FormInput
              control={control}
              name="name"
              label={t('resources.partners.fields.name')}
              placeholder={t('resources.partners.placeholders.name')}
              isRequired
            />

            <FormSelect
              control={control}
              name="partner_type"
              label={t('resources.partners.fields.partner_type')}
              options={partnerTypeOptions}
              placeholder={t('resources.partners.placeholders.partner_type')}
            />
          </div>

          {/* Referral Settings */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <FormSwitch
              control={control}
              name="hide_referral_link"
              label={t('resources.partners.fields.hide_referral_link')}
            />
          </div>

          {/* Revenue Share */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <div className="space-y-4">
              <FormInput
                control={control}
                name="revenue_share_percentage"
                label={t('resources.partners.fields.revenue_share_percentage')}
                type="number"
                placeholder={t('resources.partners.placeholders.revenue_share_percentage')}
              />

              <FormInput
                control={control}
                name="revenue_share_period_months"
                label={t('resources.partners.fields.revenue_share_period_months')}
                type="number"
                placeholder={t('resources.partners.placeholders.revenue_share_period_months')}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-6 dark:border-gray-700">
            <Button color="secondary" type="button" onPress={() => navigate('/partners')}>
              {t('resources.generic.cancel')}
            </Button>
            <Button color="primary" type="submit" isDisabled={isSubmitting} iconLeading={CheckIcon}>
              {isSubmitting ? t('resources.generic.loading') : t('resources.generic.save')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default PartnerCreatePage
